import { createServerFn } from "@tanstack/react-start";
import { sanityClient, type CaseStudy, type BlogPost } from "./sanity";

// `schema_json_ld` is deliberately not projected: the Article JSON-LD is generated
// from the document's fields (see src/routes/case-studies.$slug.tsx). `_createdAt`
// backs `datePublished` — caseStudy has no editorial publish date field.
const CASE_STUDY_PROJECTION = `{
  _id, _createdAt, _updatedAt, title, excerpt, category, company, metric, externalUrl, slug,
  coverImage{ asset->{ url, metadata { dimensions } } },
  body,
  meta_title, meta_description, keywords, canonical_url,
  og_title, og_description, og_type,
  og_image{ asset->{ url } },
  twitter_title, twitter_description, twitter_card_type
}`;

// Card-only projection for case-study grids (listing, homepage, product pages).
// Omits `body` (full Portable Text) and all SEO/OG/Twitter/schema fields — none
// of which the card renders — so those listings don't serialize whole articles.
const CASE_STUDY_CARD_PROJECTION = `{
  _id, _updatedAt, title, excerpt, category, company, metric, externalUrl, slug,
  coverImage{ asset->{ url, metadata { dimensions } } }
}`;

const BLOG_POST_FIELDS = `
  _id, _createdAt, _updatedAt, title, excerpt, category, author, readTime, publishedAt, externalUrl, slug,
  coverImage{ asset->{ url, metadata { dimensions } } },
  body,
  meta_title, meta_description, keywords, canonical_url,
  og_title, og_description, og_type,
  og_image{ asset->{ url } },
  twitter_title, twitter_description, twitter_card_type`;

const BLOG_POST_PROJECTION = `{${BLOG_POST_FIELDS}
}`;

// Single-post projection. `faq` is only rendered and only feeds JSON-LD on the
// post page itself, so it stays out of every multi-post query.
const BLOG_POST_DETAIL_PROJECTION = `{${BLOG_POST_FIELDS},
  faq[]{ question, answer }
}`;

// Card-only projection for the blog listing: just the fields the card renders.
// Deliberately omits `body` (full Portable Text) and all SEO/OG/Twitter/schema
// fields — those are only needed on the individual post page. Serializing the
// full posts for every listing page is what made /blog?page=N HTML too large.
const BLOG_CARD_PROJECTION = `{
  _id, title, excerpt, category, author, readTime, publishedAt, externalUrl, slug,
  coverImage{ asset->{ url, metadata { dimensions } } }
}`;

const BLOG_ORDER = `order(order asc, publishedAt desc, _createdAt desc)`;

export type BlogCard = Pick<
  BlogPost,
  | "_id"
  | "title"
  | "excerpt"
  | "category"
  | "author"
  | "readTime"
  | "publishedAt"
  | "externalUrl"
  | "slug"
  | "coverImage"
>;

export type BlogListing = {
  posts: BlogCard[];
  total: number;
  page: number;
  perPage: number;
  index: { title: string; slug: string }[];
};

const BLOG_PER_PAGE = 12;

export const getCaseStudiesForProduct = createServerFn({ method: "GET" })
  .inputValidator((data: { productSlug: string }) => data)
  .handler(async ({ data }): Promise<CaseStudy[]> => {
    try {
      const results = await sanityClient.fetch<CaseStudy[]>(
        `*[_type == "caseStudy" && productSlug == $slug] | order(order asc, _createdAt desc)[0...3]${CASE_STUDY_CARD_PROJECTION}`,
        { slug: data.productSlug },
      );
      return results ?? [];
    } catch (err) {
      console.error("Sanity fetch failed:", err);
      return [];
    }
  });

export const getLatestCaseStudies = createServerFn({ method: "GET" }).handler(
  async (): Promise<CaseStudy[]> => {
    try {
      const results = await sanityClient.fetch<CaseStudy[]>(
        `*[_type == "caseStudy"] | order(order asc, _createdAt desc)[0...3]${CASE_STUDY_CARD_PROJECTION}`,
      );
      return results ?? [];
    } catch (err) {
      console.error("Sanity fetch failed:", err);
      return [];
    }
  },
);

export const getAllCaseStudies = createServerFn({ method: "GET" }).handler(
  async (): Promise<CaseStudy[]> => {
    try {
      const results = await sanityClient.fetch<CaseStudy[]>(
        `*[_type == "caseStudy"] | order(order asc, _createdAt desc)${CASE_STUDY_CARD_PROJECTION}`,
      );
      return results ?? [];
    } catch (err) {
      console.error("Sanity fetch failed:", err);
      return [];
    }
  },
);

export const getAllBlogPosts = createServerFn({ method: "GET" }).handler(
  async (): Promise<BlogPost[]> => {
    try {
      const results = await sanityClient.fetch<BlogPost[]>(
        `*[_type == "blogPost"] | order(order asc, publishedAt desc, _createdAt desc)${BLOG_POST_PROJECTION}`,
      );
      return results ?? [];
    } catch (err) {
      console.error("Sanity fetch failed:", err);
      return [];
    }
  },
);

// Paginated listing used by /blog. Returns only the current page's card data
// (12 posts, no body) plus the total count and a slim title/slug index for the
// CollectionPage JSON-LD — keeping the serialized HTML small on every page.
export const getBlogListing = createServerFn({ method: "GET" })
  .inputValidator((data: { page: number }) => data)
  .handler(async ({ data }): Promise<BlogListing> => {
    try {
      const total = await sanityClient.fetch<number>(`count(*[_type == "blogPost"])`);
      const totalPages = Math.max(1, Math.ceil((total ?? 0) / BLOG_PER_PAGE));
      const page = Math.min(Math.max(1, Math.floor(data.page) || 1), totalPages);
      const start = (page - 1) * BLOG_PER_PAGE;
      const end = start + BLOG_PER_PAGE;

      const [posts, index] = await Promise.all([
        sanityClient.fetch<BlogCard[]>(
          `*[_type == "blogPost"] | ${BLOG_ORDER}[${start}...${end}]${BLOG_CARD_PROJECTION}`,
        ),
        sanityClient.fetch<{ title: string; slug: string }[]>(
          `*[_type == "blogPost" && defined(slug.current)] | ${BLOG_ORDER}{ title, "slug": slug.current }`,
        ),
      ]);

      return {
        posts: posts ?? [],
        total: total ?? 0,
        page,
        perPage: BLOG_PER_PAGE,
        index: index ?? [],
      };
    } catch (err) {
      console.error("Sanity fetch failed:", err);
      return { posts: [], total: 0, page: 1, perPage: BLOG_PER_PAGE, index: [] };
    }
  });

// Fetch card data (incl. coverImage) for a specific set of blog posts by slug.
// Used where curated posts are surfaced outside the blog (e.g. product pages)
// and need their real cover images rather than a placeholder.
export const getBlogCardsBySlugs = createServerFn({ method: "GET" })
  .inputValidator((data: { slugs: string[] }) => data)
  .handler(async ({ data }): Promise<BlogCard[]> => {
    if (!data.slugs.length) return [];
    try {
      const results = await sanityClient.fetch<BlogCard[]>(
        `*[_type == "blogPost" && slug.current in $slugs]${BLOG_CARD_PROJECTION}`,
        { slugs: data.slugs },
      );
      return results ?? [];
    } catch (err) {
      console.error("Sanity fetch failed:", err);
      return [];
    }
  });

export const getBlogPostBySlug = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }): Promise<BlogPost | null> => {
    try {
      const result = await sanityClient.fetch<BlogPost | null>(
        `*[_type == "blogPost" && slug.current == $slug][0]${BLOG_POST_DETAIL_PROJECTION}`,
        { slug: data.slug },
      );
      return result ?? null;
    } catch (err) {
      console.error("Sanity fetch failed:", err);
      return null;
    }
  });

export const getCaseStudyBySlug = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }): Promise<CaseStudy | null> => {
    try {
      const result = await sanityClient.fetch<CaseStudy | null>(
        `*[_type == "caseStudy" && slug.current == $slug][0]${CASE_STUDY_PROJECTION}`,
        { slug: data.slug },
      );
      return result ?? null;
    } catch (err) {
      console.error("Sanity fetch failed:", err);
      return null;
    }
  });
